import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { useCallback } from "react";
import { SHIPPING_PAPERWORK_QUERY } from "routes/Customer/Waste/Manifests/api";

import { APPLY_EXTRACTED_BILL_OF_LADING, EXTRACTED_BILL_OF_LADING } from "./api";
import { toApplyInput } from "./extractionReview";

/**
 * Applying a reviewed bill of lading reading.
 *
 * There is no `wasteShippingDocumentInputValidator` (unlike 7C's profile
 * apply), so nothing runs client-side before the mutation — the save either
 * succeeds or surfaces the server's rejection.
 *
 * Refetches the reading (its `candidateStatus` flips to APPLIED) and the
 * merged shipping-paperwork list, which is the only surface an applied bill of
 * lading lands on, so it shows up without a manual reload.
 * `GetShippingDocument` is not refetched here — the caller hands the saved id
 * to a surface that reads it for the first time.
 */
export function useApplyReviewedBillOfLading({
  documentId,
  facilityId,
  existingId,
  onApplied,
}: {
  documentId: string;
  facilityId: string;
  existingId?: string;
  onApplied: (savedId: string) => void;
}) {
  const alerts = useAlerts();
  const [applyMutation, { loading: applying }] = useMutation(APPLY_EXTRACTED_BILL_OF_LADING, {
    refetchQueries: [EXTRACTED_BILL_OF_LADING, SHIPPING_PAPERWORK_QUERY],
  });

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      try {
        const { data } = await applyMutation({
          variables: { input: toApplyInput(reviewed, { documentId, facilityId, existingId }) },
        });
        const savedId = data?.applyExtractedBillOfLading?.id;
        if (!savedId) {
          throw new Error("The apply did not return a saved document.");
        }
        alerts.success("Bill of lading applied.");
        onApplied(savedId);
      } catch (error) {
        alerts.error(
          "Could not apply the bill of lading",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [alerts, applyMutation, documentId, facilityId, existingId, onApplied]
  );

  return { confirm, applying };
}
